import { Ref } from 'vue';

export const useDomHighlighter = () => {
  const DEFAULT_CLASS = 'search-highlight';

  const removeHighlights = (el: HTMLElement, className = DEFAULT_CLASS) => {
    // console.log(el);
    const spans = el.querySelectorAll(`span.${className}`);
    spans.forEach((span) => {
        // console.log(span)
      const parent = span.parentNode;
      if (!parent) return;
      parent.replaceChild(document.createTextNode(span.textContent || ''), span);
      parent.normalize();
    });
  };

  const escapeRegExp = (text: string): string =>
    text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  interface HighlightConfig {
    className?: string;
    removeExistingHighlights?: boolean;
    highlightAll?: boolean;
  }

  const highlightText = (
    el: HTMLElement,
    term: string,
    config: HighlightConfig = {}
  ) => {
    const {
      className = DEFAULT_CLASS,
      removeExistingHighlights = true,
      highlightAll = true, // ✅ default to true
    } = config;
    term = term.trim();
    if (removeExistingHighlights) {
      removeHighlights(el, className);
    }
  
    if (!term) return;
  
    const keyword = term.toUpperCase();
  
    const innerHighlight = (node: Node): number => {
      let skip = 0;
  
      if (node.nodeType === 3) {
        let textNode = node as Text;
        let data = textNode.data;
        let upper = data.toUpperCase();
        let index = upper.indexOf(keyword);
  
        while (index >= 0) {
          const spannode = document.createElement('span');
          spannode.className = className;
  
          const middlebit = textNode.splitText(index);
          const afterbit = middlebit.splitText(term.length);
          const clone = middlebit.cloneNode(true);
          spannode.appendChild(clone);
          middlebit.parentNode?.replaceChild(spannode, middlebit);
  
          skip = 1;
  
          if (!highlightAll) break;
  
          // Continue from after the span
          textNode = afterbit;
          data = textNode.data;
          upper = data.toUpperCase();
          index = upper.indexOf(keyword);
        }
  
      } else if (
        node.nodeType === 1 &&
        (node as HTMLElement).childNodes &&
        !/(script|style)/i.test((node as HTMLElement).tagName)
      ) {
        const children = Array.from(node.childNodes);
        for (let i = 0; i < children.length; i++) {
          i += innerHighlight(children[i]);
        }
      }
  
      return skip;
    };
  
    innerHighlight(el);
  };

  // Wrapper for Vue DOM refs
  const highlightTextFromRef = (
    elRef: Ref<any>, // we can type more strictly later
    term: string,
    config?: HighlightConfig
  ) => {
    const el = elRef?.value?.$el ?? elRef?.value;
    if (!el || !(el instanceof HTMLElement)) return;
  
    highlightText(el, term, config);
  };

  return {
    highlightText,
    highlightTextFromRef,
    removeHighlights,
  };
};